import type { ChangeEventHandler, DragEventHandler } from 'react'
import { useCallback, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import UploadIcon from '@/modules/chatting/icons/upload.svg'
import { classNames } from '@/utils/helpers'

interface Props {
  setFile: (file: File) => void
  acceptedContentTypes: Readonly<string[]>
}

export const ChooseFile = ({ setFile, acceptedContentTypes }: Props) => {
  const [isDragging, setIsDragging] = useState(false)
  const handleDragLeave: DragEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver: DragEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }

  const acceptedAudioFormats = useMemo(
    () =>
      acceptedContentTypes.map(
        (contentType) => `.${contentType.split('/')[1]}`,
      ),
    [acceptedContentTypes],
  )

  const validateUploadingFileTypes = useCallback(
    (file: File) => {
      const extension = file.type.split('/').pop()

      return acceptedAudioFormats.includes(`.${extension}`)
    },
    [acceptedAudioFormats],
  )

  const handleDrop: DragEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
    const file = event.dataTransfer.files[0]

    if (!validateUploadingFileTypes(file)) {
      return
    }
    setFile(file)
  }
  const handleFileChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (!event.target.files) return

    const file = event.target.files[0]

    if (file && !validateUploadingFileTypes(file)) {
      return
    }
    setFile(file)
  }

  return (
    <div
      className={classNames(
        'flex w-full items-center justify-center rounded-lg border border-dashed bg-secondary-50 px-4 py-6 text-title dark:bg-secondary-600',
        {
          'border-primary-500 bg-primary-50 dark:border-primary-500 dark:bg-primary-900':
            isDragging,
        },
      )}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="space-y-4 text-center">
        <UploadIcon className="mx-auto" />
        <div className="flex items-center gap-1 text-base font-medium">
          <span>Drag & drop your files here or</span>{' '}
          <Button
            variant="link"
            className="mb-0 h-max cursor-pointer rounded-none p-0 text-base font-medium text-primary-500 underline underline-offset-[3px]"
            as="label"
            htmlFor="audio-input"
          >
            choose file
          </Button>
        </div>
      </div>
      <input
        type="file"
        id="audio-input"
        accept={acceptedContentTypes.join(',')}
        onChange={handleFileChange}
        className="!hidden"
      />
    </div>
  )
}
